import httpClient from "../axios/interceptors";
import { handleResponseError } from "helpers/utils";
import { API } from "./endpoint";
import { AxiosResponse } from "axios";

export type CreditBalance = {
  available_credit: number;
  expiring_lot: {
    remaining_amount: number;
    expires_at: string;
  } | null;
  /** เกณฑ์ขั้นต่ำเตือนให้เติมเครดิตของระบบส่งพัสดุนอก SKU (master_bangbow.minimum_parcel_credit) */
  minimum_parcel_credit: number;
  has_credit_debt?: boolean;
  debt_amount?: number;
  debt_display?: number;
};

export const getFactoryCreditBalance = async (): Promise<CreditBalance> => {
  const res: AxiosResponse = await httpClient
    .get(API.factoryCreditBalance)
    .catch(handleResponseError);
  return res.data?.data ?? { available_credit: 0, expiring_lot: null, minimum_parcel_credit: 20 };
};

export type CreditTransaction = {
  id: string;
  type: string;
  system_type: string | null;
  amount: number;
  lot_id: string | null;
  reference_id: string | null;
  order_code: string | null;
  tracking_number: string | null;
  note: string | null;
  create_date: string;
  lot_expires_at?: string | null;
  /** ข้อความภาษาไทยสำหรับแสดงในตารางประวัติ — จาก API */
  display_label_th?: string;
  /** quota = กระทบโควต้า COD (pending), balance = กระทบเครดิตดิบ */
  display_affects?: "quota" | "balance" | "both" | "none";
  /** ยอดรายการ × multiplier */
  amount_display?: number;
  /** จ่าย (เงินออก) — หน่วยเดียวกับคงเหลือบนการ์ด */
  debit_display?: number;
  /** รับ (เงินเข้า) — หน่วยเดียวกับคงเหลือบนการ์ด */
  credit_display?: number;
  /** คงเหลือก่อนรายการ — สูตรเดียวกับการ์ด (ติดลบได้) */
  remaining_before_display?: number | null;
  /** คงเหลือหลังรายการ — สูตรเดียวกับการ์ด (ติดลบได้) */
  remaining_after_display?: number | null;
  /** available_credit หลังรายการ (raw) */
  available_after?: number | null;
  /** โควต้า COD ก่อน/หลัง (× multiplier แล้ว) */
  quota_before?: number | null;
  quota_after?: number | null;
  /** ยอดเครดิตหลังรายการ — รวม "-yy" ถ้าติดลบ */
  credit_balance_after_display?: string;
  /** เลขที่ทำรายการ (ORDER order_code / PARCEL reference_no / TOPUP lot.order_code) */
  ref_display_no?: string | null;
  /** true = แถวย่อยของกลุ่ม PARCEL deduct ที่หักหลาย lot ในครั้งเดียว — ถูกรวมโชว์ที่แถวสุดท้ายของ
   * กลุ่มแล้ว ต้องซ่อนทั้งแถวนี้ที่ frontend (ดู CreditTransactionTable.tsx) */
  is_grouped_duplicate?: boolean;
  /** 2026-09-07: เฉพาะแถว TOPUP มีค่า — ใช้แสดงรายละเอียดการสั่งซื้อในคอลัมน์ "รายการ" */
  paid_amount?: number | null;
  package_expiry_days?: number | null;
  topup_person_name?: string | null;
};

export type CreditTransactionListQuery = {
  page?: number;
  limit?: number;
  date_from?: string;
  date_to?: string;
  system_type?: string;
  type?: string;
  ref_no?: string;
  tracking_number?: string;
  keyword?: string;
  export?: boolean;
};

export type CreditTransactionListResponse = {
  data: CreditTransaction[];
  total: number;
  page: number;
  limit: number;
  truncated?: boolean;
};

export const getFactoryCreditTransactions = async (
  params: CreditTransactionListQuery = {}
): Promise<CreditTransactionListResponse> => {
  const res: AxiosResponse = await httpClient
    .get(API.factoryCreditTransactions, {
      params: {
        page: params.page ?? 1,
        limit: params.limit ?? 20,
        date_from: params.date_from || undefined,
        date_to: params.date_to || undefined,
        system_type: params.system_type || undefined,
        type: params.type || undefined,
        ref_no: params.ref_no || undefined,
        tracking_number: params.tracking_number || undefined,
        keyword: params.keyword || undefined,
        export: params.export ? true : undefined,
      },
    })
    .catch(handleResponseError);
  return (
    res.data ?? {
      data: [],
      total: 0,
      page: params.page ?? 1,
      limit: params.limit ?? 20,
    }
  );
};

export type CodCreditInfo = {
  available_credit: number;
  pending_cod_credit: number;
  cod_credit_multiplier: number;
  cod_credit_limit: number;
  usable_credit: number;
  /**
   * โควต้าที่ยังว่างจริง ณ ตอนนี้ (usable_credit − pending_cod_credit) — ต่างจาก usable_credit ตรงที่
   * usable_credit คือเพดานรวม (แสดงผล) ส่วนนี้คือเพดานหักด้วยที่ถูกออเดอร์อื่นจองไปแล้ว (2026-08-11:
   * การจองไม่แตะ available_credit เลยอีกต่อไป จึงต้องหัก pending ออกเองเพื่อรู้ว่าเหลือเท่าไหร่จริง)
   */
  remaining_quota: number;
  /** lot ที่หมดอายุเร็วสุด (raw remaining_amount) — คู่กับ expiring_quota ที่คิดแล้ว */
  expiring_lot?: { remaining_amount: number; expires_at: string } | null;
  /** lot ที่หมดอายุเร็วสุด × multiplier แล้วหัก pending — คำนวณที่ API ด้วยสูตรเดียวกับการ์ด */
  expiring_quota?: number;
  /** เครดิตที่จองไว้จริงของออเดอร์เดียว (cod_credit_reserved_amount) — มีค่าเมื่อเรียกด้วย orderId */
  cost_for_order: number | null;
  /** รวมเครดิตที่จองไว้จริง/ต้องใช้ของทุกออเดอร์ที่ query — มีค่าเมื่อเรียกด้วย orderId หรือ orderIds */
  total_cost: number | null;
  /**
   * ทุกออเดอร์ที่ query มีสถานะ "reserved" ครบหรือไม่ (อ่านจาก cod_credit_reservation_status ที่จอง
   * ไว้จริงตั้งแต่สร้างออเดอร์ — ไม่ใช่การประเมินสด) null ถ้าไม่ได้ query ออเดอร์ใดเลย
   */
  sufficient: boolean | null;
  /** order_id ที่สถานะเป็น insufficient (เครดิตไม่พอจริง ยังไม่ได้จอง) จากที่ query มา */
  insufficient_order_ids: string[];
  /** มียอดหนี้เครดิต (available < 0) */
  has_credit_debt?: boolean;
  debt_amount?: number;
  debt_display?: number;
  /**
   * โควต้าก่อนนับชุดออเดอร์ที่ query (remaining_quota + ยอดที่ชุดนี้ reserved ไว้แล้ว)
   * ใช้โชว์ "เครดิตที่ใช้ได้" ใน modal ไม่ให้ลบซ้ำจาก remaining_quota
   */
  quota_before_selection?: number | null;
  /**
   * โควต้าหลังชุดนี้ (remaining_quota − ยอด insufficient ของชุดนี้)
   * ใช้โชว์ "คงเหลือ" ใน modal ให้ตรงการ์ดบนหน้าออเดอร์เมื่อจองสำเร็จครบ
   */
  quota_after_selection?: number | null;
  /**
   * ออเดอร์ COD ที่ insufficient ทั้งหมดของโรงงานนี้ (ไม่ใช่แค่ที่ query มา) เรียงตาม FIFO (เก่าสุดก่อน)
   * พร้อมยอดขาดสะสม — ออเดอร์ท้ายคิวต้อง "แซง" ต้นทุนของออเดอร์ที่เก่ากว่าที่ยังค้างอยู่ด้วย ไม่ใช่แค่
   * เทียบต้นทุนตัวเองกับ usable_credit เดี่ยวๆ (ใช้แสดง "เครดิตไม่พอ X (สะสม Y)" ในตาราง)
   */
  insufficient_orders: { order_id: string; cost: number; shortfall: number; cumulative_shortfall: number }[];
};

/**
 * วงเงินเครดิต COD ปัจจุบัน + สถานะการจองจริงของออเดอร์ (จองไปแล้วตั้งแต่สร้างออเดอร์ ไม่ใช่ตอน accept)
 * ส่ง orderId เดียวสำหรับหน้ารายละเอียดออเดอร์ หรือ orderIds (array) สำหรับหน้ารับหลายรายการ
 */
export const getFactoryCodCreditInfo = async (params?: {
  orderId?: string;
  orderIds?: string[];
}): Promise<CodCreditInfo> => {
  const res: AxiosResponse = await httpClient
    .get(API.factoryCodCreditInfo, {
      params: {
        order_id: params?.orderId,
        order_ids: params?.orderIds?.length ? params.orderIds.join(",") : undefined,
      },
    })
    .catch(handleResponseError);
  return (
    res.data?.data ?? {
      available_credit: 0,
      pending_cod_credit: 0,
      cod_credit_multiplier: 2,
      cod_credit_limit: 0,
      usable_credit: 0,
      remaining_quota: 0,
      expiring_lot: null,
      expiring_quota: 0,
      cost_for_order: null,
      total_cost: null,
      sufficient: null,
      insufficient_order_ids: [],
      insufficient_orders: [],
      quota_before_selection: null,
      quota_after_selection: null,
    }
  );
};

export type ActiveCreditLotRow = {
  /** วันที่หมดอายุ (วันปฏิทินไทย) รูปแบบ dd/mm/yyyy ปี ค.ศ. */
  date_label: string;
  /** เครดิต (คูณ multiplier แล้ว ปัดเป็นจำนวนเต็ม) ที่หมดอายุวันนี้ */
  total_display: number;
  /** จำนวนวันคงเหลือถึงวันหมดอายุ (นับเป็นวันปฏิทินไทย) */
  days_remaining: number;
};

export type ActiveCreditLotsResponse = {
  rows: ActiveCreditLotRow[];
  total_display: number;
};

/**
 * เครดิตที่ยัง active (ยังไม่หมดอายุ) รวมตามวันปฏิทินไทยที่หมดอายุ พร้อมจำนวนวันคงเหลือ — คำนวณที่
 * backend ทั้งหมด (2026-08-26: ย้ายจาก frontend มา กัน client clock เพี้ยนกระทบตัวเลข ดู docstring ของ
 * FactoryCreditService.getActiveCreditLots ฝั่ง API) ใช้เปิด modal รายละเอียด "เครดิตกำลังหมดอายุ"
 * ต่างจาก getFactoryCreditBalance ที่ให้แค่ lot ใกล้สุดตัวเดียว
 */
export const getActiveCreditLots = async (): Promise<ActiveCreditLotsResponse> => {
  const res: AxiosResponse = await httpClient
    .get(API.factoryCreditActiveLots)
    .catch(handleResponseError);
  return res.data?.data ?? { rows: [], total_display: 0 };
};

// --- เติมเครดิตเองผ่าน payment gateway (Ksher — "Phase L") ---

export type CreditPackage = {
  id: string;
  name: string;
  is_active: boolean;
  version_id: string;
  version_no: number;
  price_thb: number;
  credit_amount: number;
  expiry_days: number;
  valid_from: string;
};

/** GET /factory/orders/credit/packages — เฉพาะแพ็กเกจที่เปิดใช้งาน + มี version ปัจจุบันจริง */
export const getFactoryCreditPackages = async (): Promise<CreditPackage[]> => {
  const res: AxiosResponse = await httpClient
    .get(API.factoryCreditPackages)
    .catch(handleResponseError);
  return res.data?.data ?? [];
};

export type TopupInitiateResult = {
  transaction_id: string;
  order_code: string;
  /** URL หน้า checkout ที่ Ksher host เอง (ไม่ใช่ EMVCo QR payload) — ใช้เป็น fallback link
   * "เปิดหน้าชำระเงิน" เท่านั้น ดู qr_pay_content สำหรับ QR ที่ scan ได้จริง */
  qr_content: string;
  /** 2026-09-08: QR PromptPay จริงที่ scan ได้ — เป็น data:image base64 (render เป็น <img> ตรงๆ) หรือ
   * EMVCo string ดิบ (เอาไป encode เป็น QR เองด้วย qrcode.react) — null ถ้าดึงไม่สำเร็จ (fallback ไปเปิด
   * qr_content แทน) */
  qr_pay_content: string | null;
  expires_at: string;
  credit_amount: number;
  price_thb: number;
  /** 2026-09-08: จำนวนวันอายุการใช้งานเครดิต (จากแพ็กเกจที่เลือก) — ใช้แสดง "อายุการใช้งาน" ที่หน้า
   * checkout */
  expiry_days: number;
};

/** POST /factory/orders/credit/topup/initiate — เลือกแพ็กเกจแล้วขอ QR เพื่อจ่ายเอง (ยังไม่มีเครดิตเข้า
 * จนกว่า Ksher จะยืนยัน webhook — ดู getFactoryCreditTopupStatus / websocket reload) */
export const initiateFactoryCreditTopup = async (packageVersionId: string): Promise<TopupInitiateResult> => {
  const res: AxiosResponse = await httpClient
    .post(API.factoryCreditTopupInitiate, { package_version_id: packageVersionId })
    .catch(handleResponseError);
  return res.data?.data;
};

export type CreditTopupStatus = {
  topup_status: "PENDING" | "PROCESSING" | "COMPLETED" | "FAILED" | "EXPIRED" | null;
  credit_amount: number | null;
};

/** GET /factory/orders/credit/topup/:order_code/status — polling fallback เสริม websocket reload */
export const getFactoryCreditTopupStatus = async (orderCode: string): Promise<CreditTopupStatus | null> => {
  const res: AxiosResponse = await httpClient
    .get(API.factoryCreditTopupStatus(orderCode))
    .catch(handleResponseError);
  return res.data?.data ?? null;
};

// --- Datatable ของแท็บ "จัดการเครดิต" (มุมมองการสั่งซื้อ/เติมเครดิต, 2026-09-02) ---
// คนละ shape/endpoint กับ CreditTransaction ด้านบน (มุมมอง ledger ทั่วไป ใช้แท็บ "ประวัติการใช้เครดิต")

export type CreditTopupRow = {
  id: string;
  order_code: string | null;
  create_date: string;
  payment_type: string | null;
  topup_status: "PENDING" | "PROCESSING" | "COMPLETED" | "FAILED" | "EXPIRED" | null;
  paid_amount: number | null;
  amount: number;
  /** ชื่อ package (join credit_package_version → credit_package) — null ถ้าไม่มี package ผูกอยู่ */
  package_name: string | null;
  package_credit_amount: number | null;
  /** ชื่อผู้ทำรายการ — contact_name ถ้าแอดมินกรอกไว้ ไม่งั้น fallback ชื่อบัญชีที่ทำรายการจริง (ไม่ได้
   * โชว์เป็นคอลัมน์แยกแล้ว 2026-09-03 — แทนที่ด้วย buyer_name/buyer_phone แต่ยังส่งกลับไว้เผื่อใช้ที่อื่น) */
  person_name: string;
  /** วันหมดอายุของ lot เครดิตที่ topup นี้สร้าง — null ถ้ายังไม่ COMPLETED (ยังไม่มี lot จริง) */
  lot_expires_at: string | null;
  /** จำนวนวันคงเหลือถึงวันหมดอายุ (นับปฏิทินไทย, คำนวณที่ backend) — null คู่กับ lot_expires_at */
  lot_days_remaining: number | null;
  /** ชื่อผู้ซื้อ (contact_name ดิบๆ) — มีค่าเฉพาะ topup ที่แอดมินเติมให้เองแล้วกรอกไว้ null ถ้าโรงงาน
   * เติมเองผ่าน gateway (ไม่มีขั้นตอนกรอกฟิลด์นี้) */
  buyer_name: string | null;
  /** เบอร์โทรผู้ซื้อ (contact_phone ดิบๆ) — เงื่อนไขเดียวกับ buyer_name */
  buyer_phone: string | null;
  /** เวลาหมดอายุของ QR (เฉพาะ topup_status=PENDING จากโรงงานเติมเองผ่าน gateway) — ใช้ทำนับถอยหลังใน
   * คอลัมน์สถานะ — null สำหรับ topup อื่น (admin topup ไม่มี QR ให้หมดอายุ) */
  qr_expires_at: string | null;
  /** 2026-09-04: ใช้เรียก initiateFactoryCreditTopup ซ้ำตอนกด "คลิกเพื่อชำระเงิน" จากตาราง — backend
   * resume ให้เองถ้ายังไม่หมดอายุ (ดู docstring ของ initiateFactoryTopUp ฝั่ง API) */
  package_version_id: string | null;
};

export type CreditTopupListQuery = {
  page?: number;
  limit?: number;
  date_from?: string;
  date_to?: string;
  keyword?: string;
  export?: boolean;
};

export type CreditTopupListResponse = {
  data: CreditTopupRow[];
  total: number;
  page: number;
  limit: number;
  truncated?: boolean;
};

/** GET /factory/orders/credit/topups — datatable แท็บ "จัดการเครดิต" */
export const getFactoryCreditTopups = async (
  params: CreditTopupListQuery = {}
): Promise<CreditTopupListResponse> => {
  const res: AxiosResponse = await httpClient
    .get(API.factoryCreditTopups, {
      params: {
        page: params.page ?? 1,
        limit: params.limit ?? 20,
        date_from: params.date_from || undefined,
        date_to: params.date_to || undefined,
        keyword: params.keyword || undefined,
        export: params.export ? true : undefined,
      },
    })
    .catch(handleResponseError);
  return (
    res.data ?? {
      data: [],
      total: 0,
      page: params.page ?? 1,
      limit: params.limit ?? 20,
    }
  );
};
